import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../shared/api/client";
import type { ModuleTypeView, ModuleView } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";

/**
 * Adding or editing one module in the catalogue.
 *
 * <p>The kind is the only field here that changes how anything behaves, so it carries an
 * explanation rather than two bare words. "Standalone" and "Customisable" tell somebody nothing
 * about which one Accounting is, and choosing wrong means a shared component's version stops
 * being tracked across the projects that use it.
 */
export function ModuleDrawer({
  module,
  open,
  onClose,
  onSaved,
}: {
  module: ModuleView | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = module != null;
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [moduleTypeId, setModuleTypeId] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const types = useQuery({
    queryKey: ["module-types", "offered"],
    queryFn: () => api.get<ModuleTypeView[]>("/api/v1/module-types"),
    enabled: open,
  });

  const chosen = (types.data ?? []).find((type) => type.id === moduleTypeId) ?? null;

  useEffect(() => {
    if (!open) {
      return;
    }
    setCode(module?.code ?? "");
    setName(module?.name ?? "");
    setModuleTypeId(module?.moduleType.id ?? "");
    setDescription(module?.description ?? "");
    setError(null);
  }, [open, module]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await api.patch(`/api/v1/modules/${module.id}`, {
          name: name.trim(),
          moduleTypeId,
          description: description.trim() === "" ? null : description.trim(),
          lockVersion: module.lockVersion,
        });
      } else {
        await api.post("/api/v1/modules", {
          code: code.trim(),
          name: name.trim(),
          moduleTypeId,
          description: description.trim() === "" ? null : description.trim(),
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["modules"] });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that module.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Edit module" : "New module"}
      subtitle="A deployable part of a product, with a version of its own."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={name.trim() === "" || moduleTypeId === "" || (!editing && code.trim() === "")}
            loading={saving}
          >
            {saving ? "Saving…" : editing ? "Save" : "Create module"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {!editing && (
          <Field label="Code" required hint="Short and stable — it is not editable afterwards.">
            <Input
              autoFocus
              value={code}
              placeholder="ACCOUNTING"
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
        )}

        <Field label="Name" required>
          <Input
            value={name}
            placeholder="Accounting"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field
          label="Type"
          required
          hint="Defined under Administration → Module types, so your own kinds work the same way."
        >
          <Select
            value={moduleTypeId}
            onChange={(event) => setModuleTypeId(event.target.value)}
          >
            <option value="">Choose a type…</option>
            {(types.data ?? []).map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>
        </Field>

        {/* What the type actually decides, said in the drawer rather than left to the name. A
            type called "Third-party" tells somebody nothing about whether its version is shared. */}
        {chosen && (
          <InlineAlert tone="info">
            {chosen.description ? `${chosen.description} ` : ""}
            {chosen.sharedVersion
              ? "Releasing it records a new version of the module itself — but each project only moves when it is actually deployed there, so a customer still on the old one shows as behind rather than being quietly marked up to date."
              : "Its version belongs to one project. Releasing it affects nobody else."}
          </InlineAlert>
        )}

        <Field label="Description">
          <Textarea
            rows={3}
            value={description}
            placeholder="What this module does, and who owns it."
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
